const CHANNEL_NAME = 'pom-admin-session-sync-v1';
const STORAGE_KEY = 'pom-admin-session-sync-event';
const TAB_ID_KEY = 'pom-admin-tab-id';

export const SESSION_SYNC_ACTIONS = {
    MASQUERADE_CONTEXT_CHANGED: 'masquarade_context_changed',
} as const;

export type SessionSyncAction =
    (typeof SESSION_SYNC_ACTIONS)[keyof typeof SESSION_SYNC_ACTIONS];

export interface SessionSyncEventPayload {
    id: string;
    action: SessionSyncAction;
    originTabId: string;
    createdAt: number;
}

let cachedTabId: string | null = null;
let broadcastChannel: BroadcastChannel | null = null;

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const isValidPayload = (payload: any): payload is SessionSyncEventPayload => {
    return Boolean(
        payload &&
            typeof payload.id === 'string' &&
            typeof payload.action === 'string' &&
            typeof payload.originTabId === 'string' &&
            typeof payload.createdAt === 'number'
    );
};

const parsePayload = (rawPayload: string | null): SessionSyncEventPayload | null => {
    if (!rawPayload) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawPayload);
        return isValidPayload(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const getBroadcastChannel = () => {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
        return null;
    }

    if (!broadcastChannel) {
        broadcastChannel = new window.BroadcastChannel(CHANNEL_NAME);
    }

    return broadcastChannel;
};

export const getCurrentTabId = () => {
    if (cachedTabId) {
        return cachedTabId;
    }

    try {
        const existingTabId = sessionStorage.getItem(TAB_ID_KEY);
        if (existingTabId) {
            cachedTabId = existingTabId;
            return existingTabId;
        }
    } catch {
        // Ignore storage failures and keep going with in-memory id.
    }

    const nextTabId = createId();

    try {
        sessionStorage.setItem(TAB_ID_KEY, nextTabId);
    } catch {
        // Ignore storage failures and keep going with in-memory id.
    }

    cachedTabId = nextTabId;
    return nextTabId;
};

export const publishSessionSyncEvent = (action: SessionSyncAction): SessionSyncEventPayload => {
    const payload: SessionSyncEventPayload = {
        id: createId(),
        action,
        originTabId: getCurrentTabId(),
        createdAt: Date.now(),
    };

    const channel = getBroadcastChannel();
    if (channel) {
        channel.postMessage(payload);
    }

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore storage write failures.
    }

    return payload;
};

export const subscribeToSessionSyncEvents = (
    listener: (payload: SessionSyncEventPayload) => void
) => {
    const channel = getBroadcastChannel();

    const onBroadcastMessage = (event: MessageEvent) => {
        if (isValidPayload(event.data)) {
            listener(event.data);
        }
    };

    const onStorageEvent = (event: StorageEvent) => {
        if (event.key !== STORAGE_KEY) {
            return;
        }

        const payload = parsePayload(event.newValue);
        if (payload) {
            listener(payload);
        }
    };

    if (channel) {
        channel.addEventListener('message', onBroadcastMessage);
    }
    window.addEventListener('storage', onStorageEvent);

    return () => {
        if (channel) {
            channel.removeEventListener('message', onBroadcastMessage);
        }
        window.removeEventListener('storage', onStorageEvent);
    };
};
